import { useAppDispatch, useAppSelector } from "@/store/hooks"
import {
  setState,
  removeGlowingProperties,
  removeTradeGlowProperties,
} from "@/store/globalSlice"
import { allMapsImageData } from "@/utils/data"
import classNames from "classnames"
import gsap from "gsap"
import { useEffect, useRef, useState } from "react"
import propertyCardMovementAudioFile from "@/audio/property-card-movement.mp3"
import { createEffectAudio } from "@/utils/audioManager"
import { activeMultiplier, activeTimings } from "@/utils/animationSpeed"
import PropertyBigCardModal from "@/Modals/PropertyBigCardModal"
import Color from "color"

interface PropertySmallCardProps {
  property: ICell
  group: IPropertyGroup
  height?: number
  width?: number
  noAnimations?: boolean
  /** Replaces the card's default height/width classes. Callers that render the
   *  card somewhere tighter than a property panel (the Last Auction Results
   *  button) pass their own size here rather than trying to override the
   *  defaults, since two equally-specific Tailwind sizes resolve by stylesheet
   *  order, not by which one the caller passed. */
  sizeClassName?: string
}

const PropertySmallCard: React.FC<PropertySmallCardProps> = ({
  property,
  group,
  height = 32,
  width = 24,
  noAnimations,
  sizeClassName = "sm:h-10 sm:w-7 h-8 w-6",
}) => {
  const dispatch = useAppDispatch()
  // Narrow selectors rather than the whole slice: there is one of these per
  // property card, so a slice-wide subscription had every card on screen
  // re-rendering on every dispatch in the app.
  const mapId = useAppSelector((state) => state.global.myGame?.settings.mapId)
  const isMobileDevice = useAppSelector((state) => state.global.isMobile)
  const propertyMoveAnimations = useAppSelector(
    (state) => state.global.animationData.propertyMoveAnimations
  )
  const glowingProperties = useAppSelector(
    (state) => state.global.animationData.glowingProperties
  )
  const tradeGlowProperties = useAppSelector(
    (state) => state.global.animationData.tradeGlowProperties
  )
  const [showBigCardModal, setShowBigCardModal] = useState(false)
  const myRef = useRef<HTMLDivElement | null>(null)
  const mapImageData = allMapsImageData.find(
    (imageData) => imageData.mapId === mapId
  )
  // Glow the card for a few seconds after it is bought/traded. Driven by redux
  // so the highlight persists across the dummy-card -> real-card swap.
  const isGlowing =
    !noAnimations && !!glowingProperties?.includes(property.order)
  // Emerald glow for cards that just changed hands in a successful trade.
  // Trades apply instantly, so this in-place pulse is the only trade visual.
  const isTradeGlowing =
    !noAnimations && !!tradeGlowProperties?.includes(property.order)

  // Self-cleaning removal: whichever real card is currently showing the property
  // clears its own glow a few seconds after mounting. Because the settled card
  // in the new owner's panel mounts right when the property lands, this ends the
  // glow ~propertyAcquiredGlow ms after landing — and it can never get stuck,
  // since the timer is owned by the card's lifecycle. The dummy/flight card
  // unmounts on the swap and its timer is cleared before it fires.
  useEffect(() => {
    if (!isGlowing || property.metadata?.dummySmallCard) return
    const timeout = setTimeout(() => {
      dispatch(removeGlowingProperties([property.order]))
    }, activeTimings().propertyAcquiredGlow)
    return () => clearTimeout(timeout)
  }, [isGlowing, property.order, property.metadata?.dummySmallCard])

  // Same self-cleaning pattern for the trade glow: the card now sitting in the
  // new owner's panel owns the timer, so the glow always ends and can't stick.
  useEffect(() => {
    if (!isTradeGlowing || property.metadata?.dummySmallCard) return
    const timeout = setTimeout(() => {
      dispatch(removeTradeGlowProperties([property.order]))
    }, activeTimings().tradeSuccessGlow)
    return () => clearTimeout(timeout)
  }, [isTradeGlowing, property.order, property.metadata?.dummySmallCard])

  const imageData = mapImageData?.cellImages.find(
    (cellImage) => cellImage.order === property.order
  )

  useEffect(() => {
    if (noAnimations) return
    if (myRef.current) {
      const myMoveAnimation = propertyMoveAnimations?.find(
        (anim) => anim.propertyOrder === property.order
      )
      if (myMoveAnimation && !property.metadata?.dummySmallCard) {
        const myDummy = document.querySelector(
          `#small-card-dummy-${property.order}`
        ) as HTMLElement
        if (!myDummy) return
        const { top, left } = myDummy?.getBoundingClientRect()
        const { top: orgTop, left: orgLeft } =
          myRef.current?.getBoundingClientRect()
        const timeline = gsap.timeline({})
        const propertyMoveAnimation = createEffectAudio(
          propertyCardMovementAudioFile
        )
        const offsetParent = myRef.current.offsetParent as HTMLElement
        const parentRect = offsetParent?.getBoundingClientRect() ?? {
          top: 0,
          left: 0,
        }
        const relOrgTop = orgTop - parentRect.top
        const relOrgLeft = orgLeft - parentRect.left
        const relTop = top - parentRect.top
        const relLeft = left - parentRect.left
        propertyMoveAnimation.play()

        timeline
          .set(myRef.current, {
            top: relOrgTop,
            left: relOrgLeft,
            position: "absolute",
            scale: 1.1,
            zIndex: 120,
          })
          .to(myRef.current, {
            top: relTop,
            left: relLeft,
            duration:
              activeMultiplier() * (activeTimings().propertyTransfer / 1000),
            // Ease out so the card decelerates into the destination instead of
            // slamming in at peak velocity. Flight distance depends on the
            // recipient's panel location (a far top-corner player is ~1500px away
            // vs ~400px for a near one); with the old "power1.in", long flights hit
            // the target at huge per-frame speed and read as "skips the last bit
            // and jerks into place". "power2.inOut" arrives soft at any distance.
            ease: "power2.inOut",
            scale: 1,
            onComplete: () => {
              setTimeout(() => {
                gsap.set(myRef.current, {
                  position: "static",
                  scale: 1,
                  zIndex: 0,
                })
              }, 5000)
            },
          })
      }
    }
  }, [propertyMoveAnimations, property.metadata?.dummySmallCard])

  const getId = () => {
    if (noAnimations) return ""
    if (property.metadata?.dummySmallCard)
      return `small-card-dummy-${property.order}`
    return `small-card-${property.order}`
  }

  const handleClick = (e: React.MouseEvent) => {
    if (isMobileDevice) {
      setShowBigCardModal(true)
    } else {
      handleMouseEnter(e)
    }
  }

  const handleMouseEnter = (e: React.MouseEvent) => {
    if (isMobileDevice) return

    dispatch(
      setState({
        hoveredItem: {
          type: "property",
          propertyOrder: property.order,
          position: { x: e.clientX, y: e.clientY },
        },
      })
    )
  }

  const myColor = new Color(group.withStrip ? group.color : "white")
  const colorDark = myColor.darken(0.3)
  const colorLight = myColor.lighten(0.1)

  return (
    <>
      <div
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        data-hover-source
        ref={myRef}
        key={
          property.order + (property.metadata?.dummySmallCard ? "dummy" : "")
        }
        data-testid={
          property.metadata?.dummySmallCard
            ? undefined
            : `game.property-small-card-${property.order}`
        }
        id={getId()}
        className={classNames(
          "border-[0.8px] gap-2 border-black cursor-pointer sm:!drop-shadow-none small-card ",
          sizeClassName,
          {
            "bg-white": !group.withStrip,
            "bg-primary": group.withStrip,
            "opacity-0": property.metadata?.dummySmallCard,
            "property-acquired-glow": isGlowing,
            "property-traded-glow": isTradeGlowing,
          }
        )}
        style={{
          filter: `drop-shadow(4px 4px 2px rgba(0, 0, 0, 0.25))`,
          backgroundImage: `radial-gradient(circle at 25% 25%, ${colorLight.hex()} 0%, ${colorDark.hex()} 100%)`,
          // height: `${height / 16}rem`,
          // width: `${width / 16}rem`,
        }}
      >
        {/* {group.withStrip && (
          <div
            className="h-8 md:h-8 border-b-black border-b w-full"
            style={{
              backgroundImage: `radial-gradient(circle at center, ${myColor.hex()} 0%, ${colorDark.hex()} 70%)`,
            }}
          ></div>
        )} */}
        {imageData?.smallCardImage && (
          <>
            <div className="w-full h-full p-[2px] py-[3px]">
              <div className="border-black h-full w-full flex items-center justify-center ">
                <img
                  src={imageData.smallCardImage}
                  className="h-auto w-full"
                ></img>
              </div>
            </div>
          </>
        )}
      </div>
      {showBigCardModal && (
        <PropertyBigCardModal
          property={property}
          onClose={() => setShowBigCardModal(false)}
        />
      )}
    </>
  )
}

export default PropertySmallCard
